import { Injectable } from '@angular/core';
import { BaseService, Resource } from './base.service';
import { HttpClient } from '@angular/common/http';
import { ResourceType } from '@completion/enums';
import { CheckSheet, CheckSheetAssignment } from '@completion/models';
import { Observable } from 'rxjs';

export interface ItemRestoreOutcome {
  itemNumber: number;
  dbUpdated: boolean;
  mipsPublished: boolean;
  mipsError: string | null;
}

export interface RestorePublishResponse {
  assignment: CheckSheetAssignment;
  outcomes: ItemRestoreOutcome[];
}

export interface CheckSheetTransferCandidate {
  csaId: number;
  tagNumber: string;
  tagName: string;
  phaseNumber: string;
  siteNumber: string;
  companyNumber: string;
  status: string;
  sameTagId: boolean;
  sameTagNumber: boolean;
  samePhase: boolean;
  sameSite: boolean;
  sameCompany: boolean;
  signed: boolean;
  verified: boolean;
  hasData: boolean;
  hasPunches: boolean;
  matchScore: number;
}

export interface CheckSheetTransferPreviewItem {
  itemNumber: number;
  name: string;
  sourceStatus: string;
  targetStatus: string;
  sourceEnteredByUserId: number | null;
  sourceEnteredByUserName: string | null;
  sourceEnteredByTimestamp: string | null;
  targetEnteredByUserId: number | null;
  targetEnteredByUserName: string | null;
  targetEnteredByTimestamp: string | null;
  sourceRecordValueCount: number;
  targetRecordValueCount: number;
  sourcePunchCount: number;
  targetPunchCount: number;
  changed: boolean;
}

export interface CheckSheetTransferPreviewResponse {
  target: CheckSheetTransferCandidate;
  items: CheckSheetTransferPreviewItem[];
  changedItemCount: number;
  transferredRecordValueCount: number;
  transferredPunchCount: number;
  targetHasData: boolean;
  targetHasPunches: boolean;
}

export interface CheckSheetTransferExecuteResponse {
  target: CheckSheetAssignment;
  items: CheckSheetTransferPreviewItem[];
  changedItemCount: number;
  transferredRecordValueCount: number;
  transferredPunchCount: number;
}

export interface CheckSheetTransferPublishItemOutcome {
  itemNumber: number;
  published: boolean;
  error: string | null;
}

export interface CheckSheetTransferPublishResponse {
  items: CheckSheetTransferPublishItemOutcome[];
  assignmentPublished: boolean;
  assignmentError: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CheckSheetService extends BaseService {
  constructor(readonly http: HttpClient) {
    super(http);
  }

  @Resource(ResourceType.UpdateCheckSheet)
  updateCheckSheet(projectId: number, updatedCheckSheet: CheckSheet): Observable<CheckSheet[]> {
    return this.http.put<CheckSheet[]>(`${this.baseUrl}/projects/${projectId}/checkSheets/${updatedCheckSheet.id}`, updatedCheckSheet);
  }

  @Resource(ResourceType.GetCheckSheetMatrixTypes)
  getCheckSheetMatrixTypes(projectId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/projects/${projectId}/checkSheets/matrixTypes`);
  }

  @Resource(ResourceType.PublishCheckSheetToMips)
  syncCheckSheetAssignmentToMips(projectId: number, csaId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/sync-mips`);
  }

  @Resource(ResourceType.GetCheckSheetHistory)
  getCheckSheetHistory(projectId: number, csaId: number): Observable<CheckSheetAssignment[]> {
    return this.http.get<CheckSheetAssignment[]>(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/history`);
  }

  // Set OK status on all signed-but-not-verified check sheet assignments for a project
  @Resource(ResourceType.ToggleStatusOnSignedButNotVerifiedCsas)
  setOkStatusOnSignedButNotVerifiedCsas(projectId: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/toggleStatusOnSignedButNotVerified`, {});
  }

  @Resource(ResourceType.GetCsaByIds)
  getCsaByIds(projectId: number, csaIds: number[]): Observable<CheckSheetAssignment[]> {
    return this.http.post<CheckSheetAssignment[]>(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/byIds`, { ids: csaIds });
  }

  @Resource(ResourceType.RestoreCsaItemStatuses)
  restoreStatuses(
    projectId: number,
    csaId: number,
    itemStatuses: { itemNumber: number; status: string; enteredByUserId: number | null; enteredByTimestamp: string | null }[],
  ): Observable<CheckSheetAssignment> {
    return this.http.post<CheckSheetAssignment>(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/restore`, {
      itemStatuses,
    });
  }

  @Resource(ResourceType.PublishCsItemToMips)
  publishMipsItem(
    projectId: number,
    csaId: number,
    itemNumber: number,
  ): Observable<{ itemNumber: number; published: boolean; error: string | null }> {
    return this.http.post<{ itemNumber: number; published: boolean; error: string | null }>(
      `${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/publish-mips-item`,
      { itemNumber },
    );
  }

  getTransferCandidates(projectId: number, csaId: number): Observable<CheckSheetTransferCandidate[]> {
    return this.http.get<CheckSheetTransferCandidate[]>(
      `${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/transfer-candidates`,
    );
  }

  previewTransfer(projectId: number, csaId: number, targetCsaId: number): Observable<CheckSheetTransferPreviewResponse> {
    return this.http.post<CheckSheetTransferPreviewResponse>(
      `${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/transfer/preview`,
      { targetCsaId },
    );
  }

  transferCheckSheet(projectId: number, csaId: number, targetCsaId: number): Observable<CheckSheetTransferExecuteResponse> {
    return this.http.post<CheckSheetTransferExecuteResponse>(
      `${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/transfer`,
      { targetCsaId },
    );
  }

  publishTransferToMips(
    projectId: number,
    csaId: number,
    itemNumbers: number[],
  ): Observable<CheckSheetTransferPublishResponse> {
    return this.http.post<CheckSheetTransferPublishResponse>(
      `${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/transfer/publish-mips`,
      { itemNumbers },
    );
  }

  deleteCheckSheetAssignmentLocalOnly(projectId: number, csaId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/checksheetAssignments/${csaId}/local-only`);
  }
}
